// The Final Tribute Invoice, built from the shared documentKit so it carries the
// same brand standards as the prospectus. Financials are computed from the
// tribute type + extra leaflets so the totals are always correct.

import {
  fillField,
  buildMasthead,
  buildSignatureBlock,
  buildFooter,
  wrapDocument,
} from "./documentKit";

const WRITTEN_FEE = 18000;
const PRINTED_FEE = 24000;
const LEAFLET_PRICE = 60;

const fmt = (n) => n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const money = (n) => `R ${fmt(n)}`;

const INVOICE_CSS = `
.metablock{text-align:center;margin:-8px 0 6px;}
.metablock .meta{color:var(--slate);font-size:13px;margin:2px 0;}
.invtitle{text-align:center;font-style:italic;font-size:30px;letter-spacing:0.14em;margin:20px 0 4px;}
.invsub{text-align:center;font-style:italic;color:var(--slate);font-size:15px;margin-bottom:24px;}
.billing{display:flex;justify-content:space-between;gap:24px;margin-bottom:14px;font-size:13.5px;}
.billing .k{font-style:italic;color:var(--slate);}
.billing .right{text-align:right;}
.billing .colhead{font-variant:small-caps;letter-spacing:0.22em;color:var(--ox);font-size:11px;margin-bottom:5px;}
.linetable td .desc{color:var(--slate);font-style:italic;font-size:12px;display:block;margin-top:3px;}
.linetable td .title{color:var(--ox);}
.totes{width:100%;font-size:14px;margin-top:4px;}
.totes td{border:none;padding:5px 10px;}
.totes .lab{text-align:right;}
.totes .val{text-align:right;width:32%;}
.totes .grand .lab,.totes .grand .val{color:var(--ox);font-size:17px;padding-top:8px;}
.label{font-variant:small-caps;letter-spacing:0.28em;font-size:12px;color:var(--ox);margin:20px 0 6px;}
.pay p{font-size:13.5px;margin:3px 0;}
`;

export function buildTributeInvoiceHtml(fields = {}) {
  const {
    tributeType = "printed",
    billedTo,
    billedCity,
    deceasedName,
    invoiceNumber,
    invoiceDate,
    extraLeaflets,
  } = fields;

  const printed = tributeType !== "written";
  const baseFee = printed ? PRINTED_FEE : WRITTEN_FEE;
  const leaflets = Math.max(0, parseInt(extraLeaflets, 10) || 0);
  const leafletTotal = leaflets * LEAFLET_PRICE;
  const subtotal = baseFee + leafletTotal;

  const mainLine = printed
    ? `<tr><td><span class="title">The Printed Tribute</span><span class="desc">A seventy-two-hour anthropological tribute, printed on fine paper and couriered, fifty leaflets included.</span></td><td class="r">1</td><td class="r strongcell">${fmt(
        PRINTED_FEE
      )}</td></tr>`
    : `<tr><td><span class="title">The Written Tribute</span><span class="desc">A seventy-two-hour anthropological tribute, delivered as a print-ready file.</span></td><td class="r">1</td><td class="r strongcell">${fmt(
        WRITTEN_FEE
      )}</td></tr>`;

  const leafletLine =
    leaflets > 0
      ? `<tr><td><span class="title">Additional leaflets</span><span class="desc">Fine-paper leaflets above the fifty included, at R60 each.</span></td><td class="r">${leaflets}</td><td class="r strongcell">${fmt(
          leafletTotal
        )}</td></tr>`
      : "";

  const body = `${buildMasthead()}<div class="metablock"><div class="meta">The Raven&#8217;s Quill (Pty) Ltd &#183; Pretoria, South Africa</div><div class="meta">Company registration no.: 2025/875945/07</div></div><div class="invtitle">I N V O I C E</div><div class="invsub">The Final Tribute</div><div class="billing"><div><div class="colhead">Billed to</div><div>${fillField(
    billedTo,
    "family name, or name of person settling"
  )}</div><div>${fillField(
    billedCity,
    "city, province"
  )}</div></div><div class="right"><div><span class="k">Invoice number:</span> ${fillField(
    invoiceNumber,
    "RQ-T-2026-001"
  )}</div><div><span class="k">Invoice date:</span> ${fillField(
    invoiceDate,
    "date"
  )}</div><div><span class="k">Due:</span> On commissioning, before work begins</div><div><span class="k">In tribute to:</span> ${fillField(
    deceasedName,
    "name of the deceased"
  )}</div></div></div><table class="linetable"><tr><th>Description</th><th class="r">Qty</th><th class="r">Amount (ZAR)</th></tr>${mainLine}${leafletLine}</table><table class="totes"><tr><td class="lab">Subtotal</td><td class="val">${money(
    subtotal
  )}</td></tr><tr><td class="lab">VAT</td><td class="val">Not applicable</td></tr><tr class="grand"><td class="lab">Total due, in full, to begin</td><td class="val">${money(
    subtotal
  )}</td></tr></table><div class="label">Payment details</div><div class="pay"><p>Account name:&nbsp; The Raven&#8217;s Quill</p><p>Bank:&nbsp; First National Bank</p><p>Account number:&nbsp; 63182970137</p><p>Branch code:&nbsp; 250655</p><p>Reference:&nbsp; ${fillField(
    invoiceNumber,
    "RQ-T-2026-001"
  )}</p></div><div class="label">Notes</div><p style="font-size:13px;">The fee is settled in full to begin, so that our whole attention is on the work, and nothing follows you afterward. Work begins as soon as payment is received. The finished tribute is delivered within seventy-two hours.</p><p style="font-size:13px;">The Printed Tribute fee includes fifty leaflets. Where a larger funeral is expected and the final leaflet count is not yet settled, the base fee is paid to begin and additional leaflets, at R60 each in twenty-fives, are reconciled on delivery. The Raven&#8217;s Quill (Pty) Ltd is not registered for VAT; no value-added tax is charged. Payment by electronic funds transfer to the account above, using the reference provided.</p>${buildSignatureBlock()}${buildFooter(
    { fline: "THE RAVEN&#8217;S QUILL &#183; THE FINAL TRIBUTE" }
  )}`;

  return wrapDocument({ title: "The Final Tribute, Invoice", extraCss: INVOICE_CSS, body });
}
